import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from 'prosemirror-state'
import { Decoration, DecorationSet } from 'prosemirror-view'

const COMMENT_HIGHLIGHT_KEY = new PluginKey('commentHighlight')

interface CommentHighlightOptions {
  HTMLAttributes: Record<string, any>
}

type Highlight = { from: number; to: number; comment: string; id: string; cssClass?: string }

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    commentHighlight: {
      setCommentHighlights: (highlights: Highlight[]) => ReturnType
    }
  }
}

export const CommentHighlight = Extension.create<CommentHighlightOptions>({
  name: 'commentHighlight',

  addOptions() {
    return {
      HTMLAttributes: {
        class: 'comment-highlight',
      },
    }
  },

  addProseMirrorPlugins() {
    const key = COMMENT_HIGHLIGHT_KEY
    const pluginOptions = this.options

    return [
      new Plugin({
        key,
        state: {
          init() {
            return { decorations: DecorationSet.empty }
          },
          apply(tr, value) {
            // Map decorations through document changes
            let decorations = value.decorations.map(tr.mapping, tr.doc)

            // Update highlights if transaction has metadata
            const highlights = tr.getMeta(key) as Highlight[] | undefined
            if (highlights) {
              const newDecorations = highlights.map((hl) =>
                Decoration.inline(hl.from, hl.to, {
                  class: hl.cssClass || pluginOptions.HTMLAttributes.class,
                  'data-comment': hl.comment,
                  'data-comment-id': hl.id,
                })
              )
              decorations = DecorationSet.create(tr.doc, newDecorations)
            }

            return { decorations }
          },
        },
        props: {
          decorations(state) {
            const pluginState = key.getState(state)
            return pluginState?.decorations || DecorationSet.empty
          },
        },
      }),
    ]
  },

  addCommands() {
    return {
      setCommentHighlights:
        (highlights: Highlight[]) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            tr.setMeta(COMMENT_HIGHLIGHT_KEY, highlights)
          }
          return true
        },
    }
  },
}) 